import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  getRoleDetails,
  assignPermissionsToRole,
} from "../../../redux/Slices/RolesSlice";
import { getPermissions } from "../../../redux/Slices/PermissionsSlice";
import { FaArrowRight } from "react-icons/fa";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import i18n from "../../../i18n/i18n";

const AssignPermissions = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation("global");
  const isRTL = i18n.language === "ar";
  const textAlign = isRTL ? "right" : "left";
  const dir = isRTL ? "rtl" : "ltr";

  const [selectedPermissionIds, setSelectedPermissionIds] = useState([]);
  const [selectedPermissionsMap, setSelectedPermissionsMap] = useState({}); // Map of id -> permission object
  const [initialPermissionIds, setInitialPermissionIds] = useState([]); // Original permissions from API
  const [initialPermissionsMap, setInitialPermissionsMap] = useState({}); // Original permissions map
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [roleName, setRoleName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const searchInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      setIsFetching(true);
      try {
        // Fetch role details
        const roleResult = await dispatch(getRoleDetails(id));
        if (getRoleDetails.fulfilled.match(roleResult)) {
          const roleData = roleResult.payload?.data || roleResult.payload;
          setRoleName(roleData.name || "");
          // Store permission IDs and create a map
          const permissions = roleData.permissions || [];
          const ids = permissions.map(p => p.id || p);
          const map = {};
          permissions.forEach(p => {
            const permId = p.id || p;
            map[permId] =
              typeof p === "object"
                ? p
                : { id: permId, name: p.name || `Permission ${permId}` };
          });
          // Set both initial (original) and selected (current) permissions
          setInitialPermissionIds(ids);
          setInitialPermissionsMap(map);
          setSelectedPermissionIds(ids);
          setSelectedPermissionsMap(map);
        }
      } catch (err) {
        console.error("Failed to fetch data:", err);
      } finally {
        setIsFetching(false);
      }
    };
    fetchData();
  }, [dispatch, id]);

  const fetchPermissions = async (page = 1, search = "") => {
    try {
      const params = {
        page,
        per_page: 10,
      };
      if (search) params.search = search;
      const result = await dispatch(getPermissions(params));
      if (getPermissions.fulfilled.match(result)) {
        const response = result.payload;
        // Handle response structure: { success: true, data: { data: [...], meta: { current_page: 1, ... } } }
        const data = response?.data || response;
        const permissionsList = Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data)
            ? data
            : [];
        setAvailablePermissions(permissionsList);
        // Update selectedPermissionsMap with translation from availablePermissions
        setSelectedPermissionsMap(prev => {
          const updated = { ...prev };
          selectedPermissionIds.forEach(id => {
            if (updated[id] && !updated[id].translation) {
              const availablePerm = permissionsList.find(p => p.id === id);
              if (availablePerm && availablePerm.translation) {
                updated[id] = {
                  ...updated[id],
                  translation: availablePerm.translation,
                };
              }
            }
          });
          return updated;
        });
        // Extract pagination from meta object or data object
        // Try multiple locations: data.meta, response.meta, or data directly
        let paginationData = null;
        if (data?.meta) {
          paginationData = {
            current_page: data.meta.current_page || 1,
            last_page: data.meta.last_page || 1,
            per_page: data.meta.per_page || 10,
            total: data.meta.total || 0,
            from: data.meta.from || 0,
            to: data.meta.to || 0,
          };
        } else if (response?.meta) {
          // Check if meta is at the root level of response
          paginationData = {
            current_page: response.meta.current_page || 1,
            last_page: response.meta.last_page || 1,
            per_page: response.meta.per_page || 10,
            total: response.meta.total || 0,
            from: response.meta.from || 0,
            to: response.meta.to || 0,
          };
        } else if (
          data?.data &&
          (data.current_page !== undefined || data.last_page !== undefined)
        ) {
          paginationData = {
            current_page: data.current_page || 1,
            last_page: data.last_page || 1,
            per_page: data.per_page || 10,
            total: data.total || 0,
            from: data.from || 0,
            to: data.to || 0,
          };
        }
        setPagination(paginationData);
      }
    } catch (err) {
      console.error("Failed to fetch permissions:", err);
    }
  };

  useEffect(() => {
    if (isDropdownOpen) {
      fetchPermissions(1, searchTerm);
      setCurrentPage(1);
    }
  }, [isDropdownOpen]);

  useEffect(() => {
    if (isDropdownOpen) {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      searchTimeoutRef.current = setTimeout(() => {
        setCurrentPage(1);
        fetchPermissions(1, searchTerm);
      }, 500);
      return () => {
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }
      };
    }
  }, [searchTerm]);

  useEffect(() => {
    if (isDropdownOpen) {
      fetchPermissions(currentPage, searchTerm);
    }
  }, [currentPage]);

  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handlePermissionToggle = permission => {
    const permissionId = permission.id;
    setSelectedPermissionIds(prev => {
      if (prev.includes(permissionId)) {
        // Remove from selected
        const newMap = { ...selectedPermissionsMap };
        delete newMap[permissionId];
        setSelectedPermissionsMap(newMap);
        return prev.filter(id => id !== permissionId);
      } else {
        // Add to selected
        setSelectedPermissionsMap(prev => ({
          ...prev,
          [permissionId]: permission,
        }));
        return [...prev, permissionId];
      }
    });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setIsLoading(true);
    try {
      // Send permissions as array of IDs (ensure they are numbers)
      const permissionsArray = selectedPermissionIds.map(id => Number(id));
      const result = await dispatch(
        assignPermissionsToRole({ id, permissions: permissionsArray }),
      );
      if (assignPermissionsToRole.fulfilled.match(result)) {
        // Update initial permissions to match the saved state
        setInitialPermissionIds([...selectedPermissionIds]);
        setInitialPermissionsMap({ ...selectedPermissionsMap });
        toast.success(
          t("roles.assignPermissions.success", {
            defaultValue: "تم تعيين الصلاحيات بنجاح",
          }),
          { rtl: isRTL },
        );
        navigate("/roles");
      } else {
        toast.error(
          result.payload ||
            t("roles.assignPermissions.error", {
              defaultValue: "فشل تعيين الصلاحيات",
            }),
          { rtl: isRTL },
        );
      }
    } catch (err) {
      toast.error(
        err.message ||
          t("roles.assignPermissions.error", {
            defaultValue: "فشل تعيين الصلاحيات",
          }),
        { rtl: isRTL },
      );
    } finally {
      setIsLoading(false);
    }
  };

  const getSelectedPermissionsList = () => {
    // Get all selected permissions from the map
    // If translation is missing, try to find it from availablePermissions
    return selectedPermissionIds
      .map(id => {
        const permission = selectedPermissionsMap[id];
        if (!permission) return null;
        // If permission doesn't have translation, try to find it from availablePermissions
        if (!permission.translation) {
          const availablePerm = availablePermissions.find(p => p.id === id);
          if (availablePerm && availablePerm.translation) {
            return { ...permission, translation: availablePerm.translation };
          }
        }
        return permission;
      })
      .filter(Boolean);
  };

  if (isFetching) {
    return (
      <div
        style={{
          padding: "30px",
          backgroundColor: "#14263b",
          minHeight: "calc(100vh - 80px)",
          color: "white",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {t("common.loading", { defaultValue: "جاري التحميل..." })}
      </div>
    );
  }

  const dropdownMenuStyle = {
    position: "absolute",
    top: "calc(100% + 4px)",
    left: 0,
    right: 0,
    backgroundColor: "#1a1f2e",
    border: "1px solid #333",
    borderRadius: 8,
    zIndex: 20,
    padding: 12,
    boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
    direction: dir,
  };

  const dropdownSearchStyle = {
    width: "100%",
    padding: 10,
    borderRadius: 6,
    border: "1px solid #333",
    backgroundColor: "#14263b",
    color: "white",
    marginBottom: 10,
    direction: dir,
    textAlign: textAlign,
  };

  const dropdownEmptyStyle = {
    padding: 12,
    textAlign: "center",
    color: "#ccc",
    fontSize: 13,
  };

  const dropdownDisplayStyle = {
    width: "100%",
    padding: "12px 14px",
    backgroundColor: "#1a1f2e",
    border: "1px solid #333",
    borderRadius: 8,
    color: "white",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    cursor: "pointer",
    textAlign: textAlign,
    direction: dir,
  };

  return (
    <div
      style={{
        padding: "30px",
        backgroundColor: "#14263b",
        minHeight: "calc(100vh - 80px)",
        color: "white",
        direction: dir,
        textAlign,
      }}
    >
      <div style={{ marginBottom: "30px" }}>
        <button
          onClick={() => navigate("/roles")}
          style={{
            backgroundColor: "#666",
            color: "white",
            border: "none",
            padding: "10px",
            borderRadius: "8px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "20px",
            fontSize: "14px",
            flexDirection: isRTL ? "row" : "row-reverse",
          }}
        >
          <FaArrowRight />
          {t("common.back", { defaultValue: "الرجوع" })}
        </button>
        <h1 style={{ fontSize: "24px", fontWeight: "bold", margin: 0 }}>
          {t("roles.assignPermissions.title", {
            defaultValue: "تعيين الصلاحيات للدور",
          })}
          : {roleName}
        </h1>
      </div>

      <form
        onSubmit={handleSubmit}
        style={{
          backgroundColor: "#1b3350",
          border: "1px solid #333",
          borderRadius: "12px",
          padding: "32px",
          width: "100%",
          maxWidth: "900px",
          transition: "max-width 0.3s ease-in-out",
        }}
        className="assign-permissions-form"
      >
        <div
          style={{ marginBottom: "20px", position: "relative" }}
          ref={dropdownRef}
        >
          <label
            style={{
              display: "block",
              marginBottom: "8px",
              fontSize: "14px",
              fontWeight: "bold",
              color: "white",
            }}
          >
            {t("roles.form.permissions", { defaultValue: "الصلاحيات" })}
          </label>
          <div
            style={dropdownDisplayStyle}
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          >
            <span
              style={{
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {selectedPermissionIds.length > 0
                ? `${selectedPermissionIds.length} ${t("roles.form.selected", { defaultValue: "محدد" })}`
                : t("roles.form.selectPermissions", {
                    defaultValue: "اختر الصلاحيات",
                  })}
            </span>
            <span style={{ marginLeft: "8px", marginRight: "8px" }}>▼</span>
          </div>

          {isDropdownOpen && (
            <div style={dropdownMenuStyle} onClick={e => e.stopPropagation()}>
              <div
                style={{
                  padding: 8,
                  display: "flex",
                  gap: "8px",
                  flexDirection: "column",
                }}
              >
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder={t("roles.form.searchPermissions", {
                    defaultValue: "ابحث عن الصلاحيات",
                  })}
                  value={searchTerm}
                  onChange={e => {
                    setSearchTerm(e.target.value);
                    setTimeout(() => {
                      searchInputRef.current?.focus();
                    }, 0);
                  }}
                  onFocus={e => e.stopPropagation()}
                  onKeyDown={e => e.stopPropagation()}
                  autoFocus
                  style={dropdownSearchStyle}
                />

                {pagination &&
                  pagination.last_page &&
                  pagination.last_page > 0 && (
                    <div
                      style={{
                        padding: "8px 0",
                        borderTop: "1px solid #333",
                        background: "#14263b",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: 6,
                        }}
                      >
                        <span
                          style={{
                            color: "#ccc",
                            fontSize: 11,
                            direction: dir,
                            textAlign: textAlign,
                          }}
                        >
                          {t("common.page", { defaultValue: "صفحة" })}{" "}
                          {pagination.current_page}{" "}
                          {t("common.of", { defaultValue: "من" })}{" "}
                          {pagination.last_page}
                        </span>
                      </div>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          gap: 4,
                          flexWrap: "wrap",
                        }}
                      >
                        <button
                          type="button"
                          onClick={e => {
                            e.stopPropagation();
                            if (pagination.current_page > 1) setCurrentPage(1);
                          }}
                          disabled={pagination.current_page === 1}
                          style={{
                            padding: "4px 8px",
                            backgroundColor:
                              pagination.current_page === 1
                                ? "#2a2a2a"
                                : "#1a1f2e",
                            color:
                              pagination.current_page === 1 ? "#666" : "white",
                            border: "1px solid #444",
                            borderRadius: "4px",
                            cursor:
                              pagination.current_page === 1
                                ? "not-allowed"
                                : "pointer",
                            fontSize: 10,
                            fontWeight: "bold",
                            minWidth: "28px",
                          }}
                        >
                          ««
                        </button>
                        <button
                          type="button"
                          onClick={e => {
                            e.stopPropagation();
                            if (pagination.current_page > 1)
                              setCurrentPage(pagination.current_page - 1);
                          }}
                          disabled={pagination.current_page === 1}
                          style={{
                            padding: "4px 8px",
                            backgroundColor:
                              pagination.current_page === 1
                                ? "#2a2a2a"
                                : "#1a1f2e",
                            color:
                              pagination.current_page === 1 ? "#666" : "white",
                            border: "1px solid #444",
                            borderRadius: "4px",
                            cursor:
                              pagination.current_page === 1
                                ? "not-allowed"
                                : "pointer",
                            fontSize: 10,
                            fontWeight: "bold",
                            minWidth: "28px",
                          }}
                        >
                          ‹
                        </button>
                        {(() => {
                          const pages = [];
                          const current = pagination.current_page;
                          const last = pagination.last_page;
                          const showPages = 5;
                          let start = Math.max(
                            1,
                            current - Math.floor(showPages / 2),
                          );
                          let end = Math.min(last, start + showPages - 1);
                          if (end - start < showPages - 1)
                            start = Math.max(1, end - showPages + 1);
                          for (let i = start; i <= end; i++) pages.push(i);
                          return pages.map(page => (
                            <button
                              key={page}
                              type="button"
                              onClick={e => {
                                e.stopPropagation();
                                setCurrentPage(page);
                              }}
                              style={{
                                padding: "4px 8px",
                                backgroundColor:
                                  page === current ? "#AC2000" : "#1a1f2e",
                                color: "white",
                                border:
                                  page === current
                                    ? "1px solid #AC2000"
                                    : "1px solid #444",
                                borderRadius: "4px",
                                cursor: "pointer",
                                fontSize: 10,
                                fontWeight:
                                  page === current ? "bold" : "normal",
                                minWidth: "28px",
                              }}
                            >
                              {page}
                            </button>
                          ));
                        })()}
                        <button
                          type="button"
                          onClick={e => {
                            e.stopPropagation();
                            if (pagination.current_page < pagination.last_page)
                              setCurrentPage(pagination.current_page + 1);
                          }}
                          disabled={
                            pagination.current_page === pagination.last_page
                          }
                          style={{
                            padding: "4px 8px",
                            backgroundColor:
                              pagination.current_page === pagination.last_page
                                ? "#2a2a2a"
                                : "#1a1f2e",
                            color:
                              pagination.current_page === pagination.last_page
                                ? "#666"
                                : "white",
                            border: "1px solid #444",
                            borderRadius: "4px",
                            cursor:
                              pagination.current_page === pagination.last_page
                                ? "not-allowed"
                                : "pointer",
                            fontSize: 10,
                            fontWeight: "bold",
                            minWidth: "28px",
                          }}
                        >
                          ›
                        </button>
                        <button
                          type="button"
                          onClick={e => {
                            e.stopPropagation();
                            if (pagination.current_page < pagination.last_page)
                              setCurrentPage(pagination.last_page);
                          }}
                          disabled={
                            pagination.current_page === pagination.last_page
                          }
                          style={{
                            padding: "4px 8px",
                            backgroundColor:
                              pagination.current_page === pagination.last_page
                                ? "#2a2a2a"
                                : "#1a1f2e",
                            color:
                              pagination.current_page === pagination.last_page
                                ? "#666"
                                : "white",
                            border: "1px solid #444",
                            borderRadius: "4px",
                            cursor:
                              pagination.current_page === pagination.last_page
                                ? "not-allowed"
                                : "pointer",
                            fontSize: 10,
                            fontWeight: "bold",
                            minWidth: "28px",
                          }}
                        >
                          »»
                        </button>
                      </div>
                    </div>
                  )}

                {selectedPermissionIds.length > 0 && (
                  <button
                    type="button"
                    onClick={e => {
                      e.stopPropagation();
                      setSelectedPermissionIds([]);
                      setSelectedPermissionsMap({});
                    }}
                    style={{
                      padding: "8px 12px",
                      backgroundColor: "#f44336",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "12px",
                      fontWeight: "bold",
                      fontFamily:
                        '"Times New Roman Bold", "Times New Roman", "Times", serif',
                    }}
                  >
                    {t("roles.form.clearAll", { defaultValue: "إلغاء الكل" })}
                  </button>
                )}
              </div>

              <div
                style={{
                  maxHeight: 260,
                  overflowY: "auto",
                  overflowX: "hidden",
                  paddingBottom: "12px",
                }}
              >
                {availablePermissions.length === 0 ? (
                  <div
                    style={{
                      ...dropdownEmptyStyle,
                      direction: dir,
                      textAlign: textAlign,
                    }}
                  >
                    {t("roles.form.noPermissions", {
                      defaultValue: "لا توجد صلاحيات",
                    })}
                  </div>
                ) : (
                  availablePermissions.map((permission, index) => (
                    <label
                      key={permission.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        padding: "10px 12px",
                        borderTop: index === 0 ? "none" : "1px solid #333",
                        cursor: "pointer",
                        borderRadius: "4px",
                        direction: dir,
                        textAlign: textAlign,
                        marginBottom:
                          index === availablePermissions.length - 1 ? "0" : "0",
                      }}
                      onClick={e => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        checked={selectedPermissionIds.includes(permission.id)}
                        onChange={() => handlePermissionToggle(permission)}
                        style={{ cursor: "pointer" }}
                      />
                      <span>{permission.translation || permission.name}</span>
                    </label>
                  ))
                )}
              </div>
            </div>
          )}
        </div>

        {selectedPermissionIds.length > 0 && (
          <div
            style={{
              marginBottom: "20px",
              padding: "15px",
              backgroundColor: "#1a1f2e",
              borderRadius: "8px",
              border: "1px solid #333",
            }}
          >
            <div
              style={{
                fontSize: "14px",
                fontWeight: "bold",
                marginBottom: "10px",
                color: "white",
              }}
            >
              {t("roles.form.selectedPermissions", {
                defaultValue: "الصلاحيات المحددة",
              })}{" "}
              ({selectedPermissionIds.length}):
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
              {getSelectedPermissionsList().map(permission => (
                <span
                  key={permission.id}
                  style={{
                    padding: "6px 12px",
                    backgroundColor: "#AC2000",
                    color: "white",
                    borderRadius: "6px",
                    fontSize: "12px",
                    display: "inline-block",
                  }}
                >
                  {permission.translation || permission.name}
                  <button
                    type="button"
                    onClick={() => handlePermissionToggle(permission)}
                    style={{
                      marginLeft: "8px",
                      backgroundColor: "transparent",
                      border: "none",
                      color: "white",
                      cursor: "pointer",
                      fontSize: "14px",
                      fontWeight: "bold",
                    }}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          </div>
        )}

        <div
          style={{
            display: "flex",
            gap: "15px",
            flexDirection: isRTL ? "row-reverse" : "row",
            justifyContent: "center",
          }}
        >
          <button
            type="submit"
            disabled={isLoading}
            style={{
              backgroundColor: isLoading ? "#666" : "#AC2000",
              color: "white",
              border: "none",
              padding: "12px 24px",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: "bold",
              cursor: isLoading ? "not-allowed" : "pointer",
              opacity: isLoading ? 0.6 : 1,
            }}
          >
            {isLoading
              ? t("common.saving", { defaultValue: "جاري الحفظ..." })
              : t("btns.save", { defaultValue: "حفظ" })}
          </button>
          <button
            type="button"
            onClick={() => navigate("/roles")}
            style={{
              backgroundColor: "#6c757d",
              color: "white",
              border: "none",
              padding: "12px 24px",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: "bold",
              cursor: "pointer",
            }}
          >
            {t("btns.cancel", { defaultValue: "إلغاء" })}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AssignPermissions;
